import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { ApiError, api, type User } from './api';
import { auth } from './firebase';

/**
 * The one thing this app reads off a Firebase Auth error. Importing the real
 * `FirebaseError` type means reaching into `@firebase/util` directly — not
 * worth it for a single string field.
 */
type AuthErrorLike = { code?: string };

interface SessionValue {
  user: User | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn(email: string, password: string): Promise<void>;
  register(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Merge a fresh user object in after a profile update. */
  setUser(user: User): void;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Turn a Firebase Auth error into the `ApiError` shape the rest of the UI
 * already knows how to render, so `Login.tsx` etc. did not need to learn a
 * second error type on top of the one the backend already uses.
 *
 * Firebase's modern SDK collapses "wrong password" and "no such account" into
 * the same `auth/invalid-credential` code, which is exactly the
 * enumeration-safe behaviour §5 asked for from the old hand-built login route
 * — we get it here for free.
 */
function fromFirebaseError(err: unknown, fallback: string): ApiError {
  const code = (err as AuthErrorLike | undefined)?.code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return new ApiError(401, 'unauthorized', 'Email or password is incorrect.');
    case 'auth/too-many-requests':
      return new ApiError(429, 'rate_limited', 'Too many attempts. Please wait a moment and try again.');
    case 'auth/email-already-in-use':
      return new ApiError(409, 'conflict', 'That email is already registered. Try signing in instead.');
    case 'auth/invalid-email':
      return new ApiError(400, 'bad_request', 'Enter a valid email address.', {
        fields: [{ path: 'email', message: 'Enter a valid email address.' }],
      });
    case 'auth/weak-password':
      return new ApiError(400, 'bad_request', 'Please choose a stronger password.', {
        fields: [{ path: 'password', message: 'Please choose a stronger password.' }],
      });
    case 'auth/network-request-failed':
      return new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
    default:
      return new ApiError(0, 'error', fallback);
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<SessionValue['status']>('loading');

  /** Pull our own profile row now that Firebase has a signed-in user. */
  const loadProfile = useCallback(async () => {
    try {
      const { user: fetched } = await api.me();
      setUser(fetched);
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof ApiError && err.isAuthError) {
        setUser(null);
        setStatus('anonymous');
        return;
      }
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (auth.currentUser) await loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    // The single source of truth for "signed in or not" is Firebase itself.
    // This fires once on load with whatever Firebase already has cached
    // (instant, no network round trip needed just to know), and again on
    // every sign-in/sign-out — including one that happens in another tab.
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        void loadProfile().catch(() => setStatus('anonymous'));
      } else {
        setUser(null);
        setStatus('anonymous');
      }
    });
    return unsubscribe;
  }, [loadProfile]);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      status,
      setUser,
      refresh,
      async signIn(email, password) {
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
          throw fromFirebaseError(err, 'Could not sign in.');
        }
        // onAuthStateChanged picks up the new user and loads the profile;
        // waiting for it here keeps the caller's navigation in order.
        await loadProfile();
      },
      async register(email, password) {
        /*
         * Score the password before creating the account.
         *
         * Firebase enforces a six-character minimum and nothing else, so
         * "passwordpassword" sails through it. The zxcvbn check this app runs
         * server-side is the actual policy — the README describes the meter
         * and the binding check as one implementation, and this call is what
         * makes that true. Without it the meter is decoration: it turns red
         * and the account is created anyway.
         *
         * Ordered before `createUserWithEmailAndPassword` deliberately. After
         * it, the account already exists and rejecting the password would mean
         * deleting a real user to undo it.
         */
        const strength = await api.passwordStrength(password, email);
        if (!strength.acceptable) {
          // The headline stays the same sentence Firebase's own weak-password
          // path produces, so a rejection reads identically wherever it came
          // from. The specific reason goes on the field itself — the API
          // translates zxcvbn's keys, so this is a sentence and not
          // "extendedRepeat".
          throw new ApiError(400, 'bad_request', 'Please choose a stronger password.', {
            fields: [
              {
                path: 'password',
                message: strength.warning || 'Please choose a stronger password.',
              },
            ],
          });
        }

        try {
          await createUserWithEmailAndPassword(auth, email, password);
        } catch (err) {
          throw fromFirebaseError(err, 'Could not create the account.');
        }
        // First authenticated call to our API provisions the profile row.
        await loadProfile();
      },
      async signOut() {
        await firebaseSignOut(auth);
        setUser(null);
        setStatus('anonymous');
      },
    }),
    [user, status, refresh, loadProfile],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
